<template>
  <div class="wrapper">
    <q-banner
      class="bg-blue-1 text-grey-9"
      style="border-left: 4px solid #1976d2; margin-bottom: 16px;"
    >
      <template v-slot:avatar>
        <q-icon name="info" color="blue" />
      </template>
      <div style="font-size: 14px; line-height: 1.6;">
        <strong>Информация о пересчёте</strong>
        <p class="tw-mt-2" style="margin-bottom: 0;">
          <strong>Тип пересчета:</strong><br />
          • <strong>Мягкий пересчет</strong> - пересчитаются только работы,
          которые еще <strong>не сданы</strong><br />
          • <strong>Жесткий пересчет</strong> - пересчитаются
          <strong>все работы</strong>, включая сданные<br />
          <br />
          <em style="color: #1976d2;">
            От выбора причины (замена материала, тариф, цена) зависит тип
            пересчета (мягкий/жесткий), но сам пересчет будет
            <strong>полным</strong> - пересчитаются все параметры сметы, а не
            только материалы или цены.
          </em>
        </p>
      </div>
    </q-banner>
    <q-banner
      v-if="!data?.floor_map_ids?.length"
      class="bg-red-1 text-grey-9"
      style="border-left: 4px solid orange; margin-bottom: 16px;"
    >
      <div style="font-size: 14px; line-height: 1.6;">
        <strong>
          <q-icon name="warning" color="red" />
          Предупреждение</strong
        >
        <p style="margin-top: 8px; margin-bottom: 8px;">
          Не выбраны этажи для пересчета. Пересчет будет произведен по всем
          этажам проекта.
        </p>
      </div>
    </q-banner>

    <CustomSelect
      :value="selectedRecalcType"
      :options="recalcTypeOptions"
      option-value-key="value"
      option-label-key="label"
      label="Шаг 1: Выберите тип пересчета"
      :clearable="false"
      @change="(val) => { selectedRecalcType = val; onRecalcTypeChange() }"
      style="margin-bottom: 16px;"
    />

    <CustomSelect
      :value="selectedReason"
      :options="filteredReasonOptions"
      option-value-key="reason_code"
      option-label-key="reason_name"
      label="Шаг 2: Выберите причину изменения"
      :clearable="false"
      @change="(val) => { selectedReason = val }"
      style="margin-bottom: 16px;"
    />

    <CustomSelect
      :value="selectedWorkSetIds"
      :options="workSetsOptions"
      option-value-key="work_set_id"
      option-label-key="work_set_name"
      label="Конструктивы"
      :multiple="true"
      :clearable="true"
      @change="(val) => { selectedWorkSetIds = val }"
      style="margin-bottom: 16px;"
    />

    <CustomSelect
      :value="selectedMaterialIds"
      :options="materialsOptions"
      option-value-key="material_id"
      option-label-key="material_name"
      label="Материалы"
      :multiple="true"
      :clearable="true"
      @change="(val) => { selectedMaterialIds = val }"
      style="margin-bottom: 16px;"
    />

    <CustomInput
      :value="recalcComments"
      type="textarea"
      label="Комментарий к пересчету (опционально)"
      placeholder="Этот комментарий будет сохранен в истории изменений сметы"
      @change_input="(val) => { recalcComments = val }"
    />

    <CustomModalFooter :btn-loading="submitLoading" :onSubmit="submitRecalc" />
  </div>
</template>

<script>
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar";
import CustomInput from "@/components/form/CustomInput";
import CustomSelect from "@/components/form/CustomSelect";
import CustomModalFooter from "@/components/modal/modals/modal-footer";
import { getFlatMaterials, getFlatWorkSets, getSmetaChangeReasons, recalcProjectMaterialPrices } from "@/views/ProjectsEntry/services";
import { showInfoToast } from "@/services";

export default {
  name: "RecalcProjectMaterialPrices",
  props: {
    data: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      selectedRecalcType: null,
      selectedReason: null,
      reasonOptions: [],
      recalcComments: '',
      workSetsOptions: [],
      materialsOptions: [],
      selectedWorkSetIds: [],
      selectedMaterialIds: [],
      submitLoading: false,
    }
  },
  computed: {
    recalcTypeOptions() {
      return [
        { label: 'Мягкий пересчет (только несданные работы)', value: 'soft' },
        { label: 'Жесткий пересчет (все работы, включая сданные)', value: 'hard' }
      ]
    },
    filteredReasonOptions() {
      if (!this.selectedRecalcType) return []
      return this.reasonOptions.filter(reason => reason.recalc_type === this.selectedRecalcType)
    },
  },
  methods: {
    async getReasons() {
      const res = await getSmetaChangeReasons()
      if (res) {
        this.reasonOptions = res
      }
    },
    async loadWorkSets() {
      const projectId = this.data?.project_id
      if (!projectId) return
      const res = await getFlatWorkSets(projectId)
      if (res) this.workSetsOptions = res || []
    },
    async loadMaterials() {
      const projectId = this.data?.project_id
      if (!projectId) return
      const res = await getFlatMaterials(projectId)
      if (res) this.materialsOptions = res || []
    },
    onRecalcTypeChange() {
      this.selectedReason = null
    },
    async submitRecalc() {
      if (!this.selectedReason) return
      const projectId = this.data?.project_id
      if (!projectId) return

      const body = {
        project_id: projectId,
        work_set_ids: this.selectedWorkSetIds?.length ? this.selectedWorkSetIds : null,
        material_ids: this.selectedMaterialIds?.length ? this.selectedMaterialIds : null,
        reason_code: this.selectedReason,
        floor_map_ids: this.data?.floor_map_ids?.length ? this.data?.floor_map_ids : null,
        comments: this.recalcComments || null,
      }
      this.submitLoading = true
      const res = await recalcProjectMaterialPrices(body)
      this.submitLoading = false

      if (res) {
        this.showSucceedSnackbar()
        if (this.closeModal) this.closeModal()
        if (this.data?.onSubmit) this.data?.onSubmit()
      }
    }
  },
  mounted() {
    this.getReasons()
    this.loadWorkSets()
    this.loadMaterials()
  },
  components: { CustomSelect, CustomInput, CustomModalFooter },
  setup() {
    const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar()
    return { showSucceedSnackbar, showErrorSnackbar }
  }
};
</script>

<style scoped lang="scss">
.wrapper {
  max-width: 700px;
}
</style>
